import React, { useCallback, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import {
  Box,
  CircularProgress,
  Container,
  Typography,
  Button,
} from "@mui/material";
 
import Login from "./Login";
import WarrantyList from "./WarrantyComponents/Dashboard/CRUD/Warranty_list";
import RaiseComplaintForm from "./WarrantyComponents/Dashboard/CRUD/RaiseComplaintForm";
import ComplaintsList from "./WarrantyComponents/Dashboard/CRUD/ComplaintsList";
import EditForm from "./WarrantyComponents/Dashboard/CRUD/Editform";
import SuperAdminPage from "./WarrantyComponents/Dashboard/Admin/SuperAdminPage";
import OrganisationAdminPage from "./WarrantyComponents/Dashboard/Admin/OrganisationAdminPage";
import ManageFacilityPage from "./WarrantyComponents/Dashboard/Admin/ManageFacilityPage";
import FacilityAdminPage from "./WarrantyComponents/Dashboard/Admin/FacilityAdminPage";
import FacilitySellerListPage from "./WarrantyComponents/Dashboard/Admin/FacilitySellerListPage";
import TechnicianDashboard from "./WarrantyComponents/Dashboard/Admin/TechnicianDashboard";
import OUOperatorPage from "./WarrantyComponents/Dashboard/Admin/OUOperatorPage";
import OUUserPage from "./WarrantyComponents/Dashboard/Admin/OUUserPage";
import UserManagement from "./WarrantyComponents/Dashboard/Admin/UserManagement";
import AdminUtilsPage from "./WarrantyComponents/Dashboard/Admin/AdminUtilsPage";
import AddShop from "./WarrantyComponents/Dashboard/Admin/AddShop";
import ShopPage from "./WarrantyComponents/Dashboard/shops/ShopPage";
import TermsAndConditions from "./WarrantyComponents/TermsAndConditions";
import PrivacyPolicy from "./WarrantyComponents/PrivacyPolicy";
import ThirdParty from "./WarrantyComponents/ThirdParty";
import Footer from "./Footer";
import HomePage from "./HomePage";
import AMCLandingPage from "./AMCComponents/AMCLandingPage";
import AMCScheduleForm from "./AMCComponents/AMCScheduleForm";
import AMCScheduleList from "./AMCComponents/AMCScheduleList";
import AMCScheduleView from "./AMCComponents/AMCScheduleView";
import AdminChecklist from "./AMCComponents/AMCChecklist/AdminChecklist";
import UserChecklist from "./AMCComponents/AMCChecklist/UserChecklist";
import AdvancedAdminChecklist from "./AMCComponents/AMCChecklist/AdvancedAdminChecklist";
import AdvancedUserChecklist from "./AMCComponents/AMCChecklist/AdvancedUserChecklist";
import MembershipMgmt from "./Membership/MembershipMgmt";
import MembershipForm from "./Membership/MembershipForm";
import AdminApprovalPage from "./Membership/AdminApprovalPage";
import BulkUpload from "./Membership/BulkUpload";
import MembershipReports from "./Membership/MembershipReports";
import MembershipCard from "./Membership/MembershipCard";
import MembershipEditForm from "./Membership/MembershipEditForm";
import ContractList from "./ContractComponents/ContractList";
import ContractForm from "./ContractComponents/ContractForm";
import NewUserSignIn from "./WarrantyComponents/Auth/NewUserSignIn";
 
import { auth, firestore } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { getDocs, collection, query, where } from "firebase/firestore";
 
import MainNavbar from "./MainNavbar";
import { AuthProvider } from "./Components/AuthContext";
import FloatingChatbot from "./Components/FloatingChatbot";
 
function AppRouterWrapper() {
  return (
    <Router>
      <AuthProvider>
        <App />
      </AuthProvider>
    </Router>
  );
}
 
function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [userData, setUserData] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
 
  const isAmcRoute = location.pathname.startsWith("/amc");
  const dashboardRoutesWithOwnNavbar = [
    "/facility-admin",
    "/organisation-admin",
    "/ou-operator",
    "/ou-user",
  ];
  const hideMainNavbar = dashboardRoutesWithOwnNavbar.some((route) =>
    location.pathname.startsWith(route)
  );
  const isFacilityOperatorRole = ["seller", "facility_operator"].includes(
    String(userRole || "").trim().toLowerCase()
  );
  const isTechnicianRole = String(userRole || "").trim().toLowerCase() === "technician";
  const isTechnicianRoleValue = useCallback(
    (role) => String(role || "").trim().toLowerCase() === "technician",
    []
  );

  const getPreferredUserProfile = useCallback(async (email) => {
    const normalizedEmail = String(email || "").trim().toLowerCase();
    if (!normalizedEmail) return null;

    const profileById = new Map();
    const emailValues = Array.from(
      new Set([normalizedEmail, String(email || "").trim()].filter(Boolean))
    );

    await Promise.all(
      emailValues.map(async (emailValue) => {
        const profileQuery = query(
          collection(firestore, "UserProfileMaster"),
          where("email", "==", emailValue)
        );
        const profileSnapshot = await getDocs(profileQuery);
        profileSnapshot.docs.forEach((profileDoc) => {
          profileById.set(profileDoc.id, {
            id: profileDoc.id,
            ...profileDoc.data(),
          });
        });
      })
    );

    const profiles = Array.from(profileById.values());
    if (!profiles.length) return null;

    return (
      profiles.find((profile) => isTechnicianRoleValue(profile.role)) ||
      profiles[0]
    );
  }, [isTechnicianRoleValue]);

  const persistProfileSession = useCallback((email, profile, fallbackDisplayName = "") => {
    const role = profile?.role || "customer";
    const tenantId = profile?.tenantId || profile?.tenantid || null;
    const displayName =
      profile?.displayName || profile?.adminName || fallbackDisplayName || "";

    sessionStorage.setItem("userRole", role);
    sessionStorage.setItem("userEmail", email);
    sessionStorage.setItem("userDisplayName", displayName);

    if (profile) {
      sessionStorage.setItem("userProfile", JSON.stringify(profile));
    } else {
      sessionStorage.removeItem("userProfile");
    }

    if (tenantId) {
      sessionStorage.setItem("tenantId", tenantId);
    } else {
      sessionStorage.removeItem("tenantId");
    }

    return { role, tenantId, displayName };
  }, []);

  const clearSession = useCallback(() => {
    sessionStorage.removeItem("userRole");
    sessionStorage.removeItem("userEmail");
    sessionStorage.removeItem("userDisplayName");
    sessionStorage.removeItem("userProfile");
    sessionStorage.removeItem("tenantId");
    sessionStorage.removeItem("tenantName");
    sessionStorage.removeItem("otpLoggedIn");
  }, []);
 
  // ── CHANGE 1: onLoginSuccess — called by Login.js after OTP or Google auth ─
  // This is the missing link. Login.js sets sessionStorage and then calls this.
  // This sets isLoggedIn=true in App state, which re-renders the /login route
  // guard and redirects to / automatically. Without this, navigate("/") in
  // Login.js changes the URL but isLoggedIn is still false so the route guard
  // sends the user straight back to /login.
  const handleLoginSuccess = ({ email, role, tenantId, returnUrl }) => {
    setUserRole(role);
    setUserData({ email, uid: null });
    setIsLoggedIn(true);
    if (returnUrl) {
      navigate(returnUrl, { replace: true });
    } else if (String(role || "").trim().toLowerCase() === "technician") {
      navigate("/technician", { replace: true });
    } else {
      navigate("/");
    }
  };
 
  useEffect(() => {
    // ── CHANGE 2: removed location.pathname and navigate from dependency array ─
    // Your original code had: }, [navigate, location.pathname]);
    // This caused onAuthStateChanged to re-register on every page navigation.
    // On re-registration Firebase fires the callback once immediately — if the
    // OTP session check runs during that firing and finds user=null (which it
    // always will for OTP users), it clears sessionStorage and logs out the user.
    // Fix: empty array [] so the listener is registered exactly once on mount.
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // ── Firebase authenticated user (Google login) ─────────────────────
        const rawSuperAdmins = [
          process.env.REACT_APP_SUPERADMIN_EMAIL1,
          process.env.REACT_APP_SUPERADMIN_EMAIL2,
          process.env.REACT_APP_SUPERADMIN_EMAIL3,
          process.env.REACT_APP_SUPERADMIN_EMAIL4,
          process.env.REACT_APP_SUPERADMIN_EMAIL5,
        ];
        const superAdminList = rawSuperAdmins
          .filter((email) => email && email.trim() !== "")
          .map((email) => email.trim());
 
        if (superAdminList.includes(user.email)) {
          setUserRole("superadmin");
          setUserData({
            email: user.email,
            displayName: user.displayName || "Super Admin",
            uid: user.uid,
            role: "superadmin",
          });
          setIsLoggedIn(true);
          sessionStorage.setItem("userRole", "superadmin");
          sessionStorage.setItem("userEmail", user.email);
          sessionStorage.removeItem("tenantId");
          return;
        }
 
        try {
          const userProfile = await getPreferredUserProfile(user.email);

          if (userProfile) {
            const { role } = persistProfileSession(
              user.email,
              userProfile,
              user.displayName
            );

            setUserRole(role);

            setUserData({
              email: user.email,
              displayName: user.displayName,
              uid: user.uid,
            });


            setIsLoggedIn(true);

            if (isTechnicianRoleValue(role) && window.location.pathname !== "/technician") {
              navigate("/technician", { replace: true });
            }
          } else {
            console.error("User profile does not exist for:", user.email);
            signOut(auth);
            setIsLoggedIn(false);
            sessionStorage.removeItem("userRole");
            sessionStorage.removeItem("tenantId");
            navigate("/login");
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
          setIsLoggedIn(false);
          sessionStorage.removeItem("userRole");
          sessionStorage.removeItem("tenantId");
        }
      } else {
        // ── Firebase user is null ─────────────────────────────────────────
        // ── CHANGE 3: OTP session restore ────────────────────────────────
        // Your original code cleared sessionStorage whenever user=null.
        // OTP users never have a Firebase session so user is ALWAYS null for them.
        // Fix: check for otpLoggedIn flag before clearing — if all 3 keys exist
        // it means an OTP user is actively logged in, so restore state instead.
        const otpLoggedIn = sessionStorage.getItem("otpLoggedIn") === "true";
        const storedRole = sessionStorage.getItem("userRole");
        const storedEmail = sessionStorage.getItem("userEmail");
 
        if (otpLoggedIn && storedRole && storedEmail) {
          // Active OTP session — restore state, do NOT clear sessionStorage
          try {
            const userProfile = await getPreferredUserProfile(storedEmail);
            const { role } = persistProfileSession(storedEmail, userProfile);
            setUserRole(role);
            setUserData({ email: storedEmail, uid: null });
            setIsLoggedIn(true);

            if (isTechnicianRoleValue(role) && window.location.pathname !== "/technician") {
              navigate("/technician", { replace: true });
            }
          } catch (error) {
            console.error("Error restoring OTP profile:", error);
            setUserRole(storedRole);
            setUserData({ email: storedEmail, uid: null });
            setIsLoggedIn(true);
          }
        } else {
          // No valid session — clear and redirect
          setIsLoggedIn(false);
          setUserData(null);
          setUserRole("");
          sessionStorage.removeItem("userRole");
          sessionStorage.removeItem("tenantId");
          sessionStorage.removeItem("userEmail");
          sessionStorage.removeItem("otpLoggedIn");
        }
      }
    });
 
    return () => unsubscribe();

  }, [
    clearSession,
    getPreferredUserProfile,
    isTechnicianRoleValue,
    navigate,
    persistProfileSession,
  ]);

 
  if (isLoggedIn === null) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Authenticating... Please wait.</Typography>
      </Box>
    );
  }
 
  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      {location.pathname !== "/login" && !hideMainNavbar && (
        <MainNavbar
          userData={userData}
          userRole={userRole}
          isAmcRoute={isAmcRoute}
        />
      )}

      <Box component="main" sx={{ flexGrow: 1 }}>
        <Routes>
          {/* ── CHANGE 4: pass onLoginSuccess prop to Login ── */}
          <Route
            path="/"
            element={
              isLoggedIn ? <HomePage /> : <Navigate to="/login" replace />
            }
          />
          <Route
            path="/login"
            element={
              isLoggedIn
                ? <Navigate to="/" replace />
                : <Login onLoginSuccess={handleLoginSuccess} />
            }
          />
          <Route
            path="/HomePage"
            element={
              isLoggedIn ? <HomePage /> : <Navigate to="/login" replace />
            }
          />
          <Route
            path="/amc/home"
            element={
              isLoggedIn ? <AMCLandingPage /> : <Navigate to="/login" replace />
            }
          />
          <Route
            path="/amc/schedule/list"
            element={
              isLoggedIn ? (
                <AMCScheduleList />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/warranty_list"
            element={
              isLoggedIn && ["customer", "shop", "admin"].includes(userRole) ? (
                <WarrantyList />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route
            path="/editform/:id"
            element={
              isLoggedIn && ["customer", "shop"].includes(userRole) ? (
                <EditForm />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route
            path="/raisecomplaint/:id"
            element={
              isLoggedIn && ["customer", "shop"].includes(userRole) ? (
                <RaiseComplaintForm />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route
            path="/complaints_list"
            element={
              isLoggedIn &&
              (["customer", "seller", "facility_operator", "admin", "facility_admin", "organisation_admin", "OU Admin"].includes(userRole) || isTechnicianRole) ? (
                <ComplaintsList />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route
            path="/user-management"
            element={
              isLoggedIn && ["admin", "superadmin"].includes(userRole) ? (
                <UserManagement />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route
            path="/admin-utils"
            element={
              isLoggedIn ? <AdminUtilsPage /> : <Navigate to="/login" replace />
            }
          />
          <Route
            path="/addshop"
            element={
              isLoggedIn && userRole === "admin" ? (
                <AddShop />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route
            path="/ShopPage"
            element={
              isLoggedIn && isFacilityOperatorRole ? (
                <ShopPage />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route
            path="/technician"
            element={
              isLoggedIn && isTechnicianRole ? (
                <TechnicianDashboard />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route
            path="/amc/schedule/new"
            element={
              isLoggedIn && !isTechnicianRole ? (
                <AMCScheduleForm />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route
            path="/amc/schedule/edit/:id"
            element={
              isLoggedIn && !isTechnicianRole ? (
                <AMCScheduleForm />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route
            path="/amc/schedule/view/:id"
            element={
              isLoggedIn ? (
                <AMCScheduleView />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/amc/checklist/admin"
            element={
              isLoggedIn && userRole === "admin" ? (
                <AdminChecklist email={userData?.email} />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route
            path="/amc/checklist/admin/advanced"
            element={
              isLoggedIn && userRole === "admin" ? (
                <AdvancedAdminChecklist />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route
            path="/amc/checklist/user"
            element={
              isLoggedIn ? (
                <UserChecklist email={userData?.email} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/amc/checklist/user/advanced"
            element={
              isLoggedIn ? (
                <AdvancedUserChecklist />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          {/* --- SuperAdmin Route --- */}
          <Route
            path="/superadmin"
            element={
              isLoggedIn && userRole === "superadmin" ? (
                <SuperAdminPage currentUser={userData} />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />

          {/* ✅ --- ADDED CONTRACT ROUTES --- ✅ */}
          <Route
            path="/contracts/list"
            element={
              isLoggedIn && ["admin", "user", "customer"].includes(userRole) ? (
                <ContractList />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route
            path="/contracts/new"
            element={
              isLoggedIn && ["admin", "user"].includes(userRole) ? (
                <ContractForm />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route
            path="/contracts/edit/:contractId"
            element={
              isLoggedIn && ["admin", "user"].includes(userRole) ? (
                <ContractForm />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          {/* ✅ --- END CONTRACT ROUTES --- ✅ */}

          {/* ✅ --- ADDED MEMBERSHIP ROUTES --- ✅ */}
          <Route
            path="/membership"
            element={
              isLoggedIn && ["admin", "customer"].includes(userRole) ? (
                <MembershipMgmt />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route path="/membership-form" element={<MembershipForm />} />
          <Route
            path="/membership/edit/:id"
            element={
              isLoggedIn ? (
                <MembershipEditForm />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/membership/approval"
            element={
              isLoggedIn && ["admin"].includes(userRole) ? (
                <AdminApprovalPage />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route
            path="/membership/reports"
            element={
              isLoggedIn && ["admin"].includes(userRole) ? (
                <MembershipReports />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route
            path="/membership/bulk-upload"
            element={
              isLoggedIn && ["admin"].includes(userRole) ? (
                <BulkUpload />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route
            path="/membership/card"
            element={
              isLoggedIn ? (
                <MembershipCard />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* ✅ --- END MEMBERSHIP ROUTES --- ✅ */}

          {/* --- Public Legal Routes --- */}
          <Route
            path="/newusersignin"
            element={<NewUserSignIn />}
          />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/third-party" element={<ThirdParty />} />


          {/* ✅ --- ADDED FACILITY ROUTES --- ✅ */}
          <Route
            path="/manage-facility"
            element={
              isLoggedIn && ["admin"].includes(userRole) ? (
                <ManageFacilityPage />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          {/* ✅ --- ADDED FACILITY & ORGANIZATION ROUTES --- ✅ */}
          <Route
            path="/facility-admin"
            element={
              isLoggedIn && userRole === "facility_admin" ? (
                <FacilityAdminPage />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route
            path="/facility-admin/sellers"
            element={
              isLoggedIn && userRole === "facility_admin" ? (
                <FacilitySellerListPage />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route
            path="/organisation-admin"
            element={
              isLoggedIn && ["organisation_admin", "OU Admin"].includes(userRole) ? (
                <OrganisationAdminPage />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route
            path="/ou-operator"
            element={
              isLoggedIn && userRole === "OU Operator" ? (
                <OUOperatorPage />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          <Route
            path="/ou-user"
            element={
              isLoggedIn && ["OU User", "organisation_operator"].includes(userRole) ? (
                <OUUserPage />
              ) : (
                <Navigate
                  to={isLoggedIn ? "/access-denied" : "/login"}
                  replace
                />
              )
            }
          />
          {/* ✅ --- END FACILITY & ORGANIZATION ROUTES --- ✅ */}

          {/* --- Fallback Routes --- */}
=
 
          
          <Route
            path="/access-denied"
            element={
              <Container sx={{ textAlign: "center", mt: 5 }}>
                <Typography variant="h4" gutterBottom>Access Denied</Typography>
                <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate("/")}>Go Home</Button>
              </Container>
            }
          />
          <Route
            path="*"
            element={
              <Container sx={{ textAlign: "center", mt: 5 }}>
                <Typography variant="h4" gutterBottom>404 - Page Not Found</Typography>
                <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate("/")}>Go Home</Button>
              </Container>
            }
          />
        </Routes>
      </Box>
 
      {location.pathname !== "/login" && <Footer />}
      
      {/* Floating Chatbot */}
      <FloatingChatbot isLoggedIn={isLoggedIn} />
    </Box>
  );
}
 
export default AppRouterWrapper;



